/**
 * Monte le Live Copilot quand un appel est en cours — et le laisse monté après.
 *
 * Séparé de LiveCopilot pour que le panneau reste un composant présentationnel
 * testable seul, et que toute la logique « quand l'afficher » tienne ici.
 *
 * Deux règles, apprises d'un vrai appel :
 *
 *  1. La visibilité ne se déduit PAS du statut du téléphone. Le SDK Twilio
 *     repasse le statut à « ready » au moment où le correspondant décroche :
 *     la liste des statuts « en appel » ne le contenait pas, donc le panneau
 *     disparaissait exactement quand la conversation commençait. C'est la
 *     présence d'un appel actif (`activeInfo.ref`) qui fait foi.
 *  2. Raccrocher ne ferme pas le panneau. Le compte rendu de fin d'appel
 *     n'arrive que quelques secondes APRÈS le raccroché : le fermer à ce
 *     moment-là revenait à jeter l'analyse. Seul le commercial le ferme.
 *  3. Un appel jamais décroché (pas de réponse, échec) n'a rien à analyser :
 *     le panneau l'apprend par `endedCall.answered` et n'annonce pas un
 *     compte rendu qui ne viendra jamais.
 *
 * Le Copilot reste masquable et sa fermeture n'interrompt rien : le backend
 * continue de transcrire et d'analyser. C'est cohérent avec son statut de
 * service auxiliaire — l'appel ne dépend pas de lui, l'inverse non plus.
 */
import { useEffect, useRef, useState } from "react";
import { useTwilio } from "@/context/TwilioVoice";
import LiveCopilot from "./LiveCopilot";

/** Événement fenêtre qui rouvre un copilote masqué (bouton de la barre d'appel). */
export const COPILOT_SHOW_EVENT = "prospee:copilot-show";
export const showCopilot = () => window.dispatchEvent(new CustomEvent(COPILOT_SHOW_EVENT));

export default function CopilotHost({ onVisibleChange }) {
  const tw = useTwilio();
  const [session, setSession] = useState(null);
  const [hidden, setHidden] = useState(false);
  // Le correspondant a-t-il décroché ? Connu au terme de l'appel seulement.
  const [answered, setAnswered] = useState(true);
  const secondsRef = useRef(0);

  const activeRef = tw?.activeInfo?.ref || null;
  const companyId = tw?.activeInfo?.company_id;
  const companyName = tw?.activeInfo?.company_name;
  const objective = tw?.activeInfo?.objective;

  // Un nouvel appel remplace la session suivie et remet le panneau en avant :
  // masquer valait pour l'appel précédent, pas pour tous les suivants.
  useEffect(() => {
    if (!activeRef) return;
    setSession({ id: activeRef, companyId, companyName, objective });
    setHidden(false);
    setAnswered(true);
  }, [activeRef, companyId, companyName, objective]);

  // Masqué par erreur pendant l'appel : le bouton « Copilote » de la barre
  // d'appel le fait revenir, avec sa transcription (le canal n'a pas bougé).
  useEffect(() => {
    const show = () => setHidden(false);
    window.addEventListener(COPILOT_SHOW_EVENT, show);
    return () => window.removeEventListener(COPILOT_SHOW_EVENT, show);
  }, []);

  const live = Boolean(activeRef) && activeRef === session?.id;
  // Issue de l'appel : un appel jamais décroché n'a rien à analyser, et le
  // panneau ne doit pas annoncer un compte rendu qui ne viendra jamais.
  // `endedCall` est effacé dès que la fenêtre de qualification se ferme : on
  // lit la valeur tant qu'elle est là, on la retient ensuite.
  const ended = tw?.endedCall;
  const endedHere = Boolean(ended?.ref) && ended.ref === session?.id;
  useEffect(() => { if (endedHere) setAnswered(ended.answered !== false); },
            [endedHere, ended]);
  const callAnswered = endedHere ? ended.answered !== false : answered;
  // Le chronomètre repart de zéro à l'appel suivant : on retient la dernière
  // valeur vue pour que le compte rendu affiche la durée réelle de l'appel.
  const seconds = live ? (tw?.callSeconds || 0) : secondsRef.current;
  useEffect(() => { if (live) secondsRef.current = tw?.callSeconds || 0; },
            [live, tw?.callSeconds]);

  // La coquille (CrmLayout) apprend quand le panneau est à l'écran : elle
  // replie le menu de gauche pour lui laisser la place, et le rétablit après.
  const visible = Boolean(session) && !hidden;
  useEffect(() => { onVisibleChange?.(visible); }, [visible, onVisibleChange]);

  if (!visible) return null;
  return (
    <LiveCopilot
      sessionId={session.id}
      live={live}
      answered={live || callAnswered}
      callSeconds={seconds}
      companyName={session.companyName}
      companyId={session.companyId}
      objective={session.objective}
      onClose={() => setHidden(true)}
    />
  );
}
